import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PiiUnhideModalComponent } from '../common/pii-unhide-modal/pii-unhide-modal.component';
import { PiiAuditService } from './pii-audit.service';
import { AlertService } from './alert.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class PiiGridService {
  
  constructor(
    private modalService: NgbModal,
    private piiAuditService: PiiAuditService,
    private alertService: AlertService,
    private storageService: StorageService
  ) {}

  /**
   * Show PII unmask modal and handle the row unmasking
   */
  unmaskRow(rowData: any, gridApi: any): void {
    // Check if already unmasked
    if (rowData._piiUnmasked) {
      // Re-mask the row
      rowData._piiUnmasked = false;
      
      // Get all row nodes and find the one matching our data
      const rowNode: any[] = [];
      gridApi.forEachNode((node: any) => {
        if (node.data === rowData) {
          rowNode.push(node);
        }
      });
      
      // Refresh the cells for this row
      if (rowNode.length > 0) {
        gridApi.refreshCells({ 
          rowNodes: rowNode,
          force: true 
        });
      }
      
      return;
    }

    // Show modal for unmasking
    const modalRef = this.modalService.open(PiiUnhideModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg'
    });

    // Set modal properties
    modalRef.componentInstance.fieldName = 'All PII Fields';
    modalRef.componentInstance.userId = this.getEntityId(rowData);
    modalRef.componentInstance.context = 'grid-row';

    // Handle modal result
    modalRef.result.then((result) => {
      if (result) {
        this.logAuditAndUnmaskRow(result, rowData, gridApi);
      }
    }).catch(() => {
      // Modal was dismissed
    });
  }

  /**
   * Log audit and unmask the entire row
   */
  private logAuditAndUnmaskRow(modalResult: any, rowData: any, gridApi: any): void {
    const currentUser = this.storageService.getUserName();
    
    const auditData = {
      userId: this.getEntityId(rowData),
      fieldName: 'All PII Fields in Row',
      reasonId: modalResult.reasonId,
      reasonDescription: modalResult.reason || 'Unknown reason',
      comment: modalResult.comment,
      accessedBy: currentUser || 'unknown',
      timestamp: new Date().toISOString(),
      context: 'grid-row',
      
      // Entity context
      entityType: this.getEntityType(rowData),
      entityMobile: this.getEntityMobile(rowData),
      transactionId: rowData.transactionId || '',
      referenceId: this.getReferenceId(rowData)
    };

    // Log the audit
    this.piiAuditService.logPiiAccess(auditData).subscribe({
      next: (response) => {
        // Set unmask flag on row data
        rowData._piiUnmasked = true;
        
        // Refresh all cells in the row to show unmasked data
        // Get all row nodes and find the one matching our data
        const rowNode: any[] = [];
        gridApi.forEachNode((node: any) => {
          if (node.data === rowData) {
            rowNode.push(node);
          }
        });
        
        // Refresh the cells for this row
        if (rowNode.length > 0) {
          gridApi.refreshCells({ 
            rowNodes: rowNode,
            force: true 
          });
        }
        
        this.alertService.successAlertWithMessage('Row PII data unmasked successfully');
      },
      error: (error) => {
        // Even if audit logging fails, allow unmask for demo
        rowData._piiUnmasked = true;
        
        // Get all row nodes and find the one matching our data
        const rowNode: any[] = [];
        gridApi.forEachNode((node: any) => {
          if (node.data === rowData) {
            rowNode.push(node);
          }
        });
        
        // Refresh the cells for this row
        if (rowNode.length > 0) {
          gridApi.refreshCells({ 
            rowNodes: rowNode,
            force: true 
          });
        }
        
        this.alertService.successAlertWithMessage('Row PII data unmasked successfully (Audit logging failed)');
      }
    });
  }

  /**
   * Check if a row has PII fields that can be unmasked
   */
  rowHasPiiFields(rowData: any): boolean {
    // Check for common PII fields
    const piiFields = [
      'emiratesId',
      'passportNumber',
      'mobileNumber',
      'mobile',
      'contactNumber',
      'bankAccountNumber',
      'iban',
      'shareHolderHit',
      'kycStatus',
      'currentTrack'
    ];
    
    // Check if any PII field exists and has data
    return piiFields.some(field => rowData[field] && rowData[field].toString().trim() !== '');
  }

  /**
   * Get entity ID from row data
   */
  private getEntityId(rowData: any): string {
    return rowData.userId || 
           rowData.merchantId || 
           rowData.customerId || 
           rowData.id || 
           'unknown';
  }

  /**
   * Get entity type from row data
   */
  private getEntityType(rowData: any): string {
    if (rowData.userType) return rowData.userType.toLowerCase();
    if (rowData.entityType) return rowData.entityType.toLowerCase();
    if (rowData.transactionId) return 'transaction';
    if (rowData.merchantId) return 'merchant';
    if (rowData.customerId) return 'customer';
    return 'unknown';
  }

  /**
   * Get entity mobile from row data
   */
  private getEntityMobile(rowData: any): string {
    return rowData.mobileNumber || 
           rowData.mobile || 
           rowData.contactNumber || 
           rowData.phone || 
           'unknown';
  }

  /**
   * Get reference ID from row data
   */
  private getReferenceId(rowData: any): string {
    return rowData.transactionId || 
           rowData.orderId || 
           rowData.referenceId || 
           rowData.id || 
           'unknown';
  }
} 